import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, DollarSign, Calendar } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { addDays, startOfWeek, addWeeks, format } from "date-fns";

export default function CashFlowPanel({ invoices = [], purchases = [] }) {
  const today = new Date();

  const cashFlowData = useMemo(() => {
    const weeks = [];
    for (let i = 0; i < 8; i++) {
      const weekStart = startOfWeek(addWeeks(today, i), { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);
      const label = `Vk ${format(weekStart, "d.M")}`;

      const incoming = invoices
        .filter((inv) => ["sent", "overdue", "reminder"].includes(inv.status))
        .filter((inv) => {
          if (!inv.due_date) return false;
          const d = new Date(inv.due_date);
          return d >= weekStart && d <= weekEnd;
        })
        .reduce((s, inv) => s + (Number(inv.total_amount ?? inv.total) || 0), 0);

      const outgoing = purchases
        .filter((p) => !["paid", "rejected"].includes(p.status))
        .filter((p) => {
          if (!p.due_date) return false;
          const d = new Date(p.due_date);
          return d >= weekStart && d <= weekEnd;
        })
        .reduce((s, p) => s + (Number(p.total_amount) || 0), 0);

      weeks.push({
        label,
        incoming: Math.round(incoming),
        outgoing: Math.round(outgoing),
        net: Math.round(incoming - outgoing),
      });
    }
    return weeks;
    // eslint-disable-next-line
  }, [invoices, purchases]);

  const totalIncoming = invoices
    .filter((i) => ["sent", "overdue", "reminder"].includes(i.status))
    .reduce((s, i) => s + (Number(i.total_amount ?? i.total) || 0), 0);
  const totalOutgoing = purchases
    .filter((p) => !["paid", "rejected"].includes(p.status))
    .reduce((s, p) => s + (Number(p.total_amount) || 0), 0);
  const netCashFlow = totalIncoming - totalOutgoing;

  const todayStr = today.toISOString().split("T")[0];
  const next7 = invoices.filter((inv) => {
    if (!["sent", "overdue"].includes(inv.status)) return false;
    if (!inv.due_date) return false;
    return inv.due_date >= todayStr && inv.due_date <= addDays(today, 7).toISOString().split("T")[0];
  });
  const overdueInvoices = invoices.filter((inv) => ["overdue", "reminder", "collection"].includes(inv.status));

  return (
    <div className="space-y-4" data-testid="cash-flow-panel">
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-emerald-400">{totalIncoming.toFixed(0)} €</p>
            <p className="text-xs text-muted-foreground">Odotettavia tuloja</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingDown className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-red-400">{totalOutgoing.toFixed(0)} €</p>
            <p className="text-xs text-muted-foreground">Avoimia menoja</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <DollarSign className={`w-5 h-5 mx-auto mb-1 ${netCashFlow >= 0 ? "text-emerald-400" : "text-red-400"}`} />
            <p className={`text-xl font-bold ${netCashFlow >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {netCashFlow.toFixed(0)} €
            </p>
            <p className="text-xs text-muted-foreground">Nettokassavirta</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Kassavirta — seuraavat 8 viikkoa</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={cashFlowData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
              <Tooltip
                formatter={(v) => `${v} €`}
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }}
              />
              <Legend />
              <Bar dataKey="incoming" name="Tulot" fill="#10b981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="outgoing" name="Menot" fill="#ef4444" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {next7.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4 text-amber-400" /> Erääntyvät seuraavat 7 päivää ({next7.length} kpl)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {next7.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium">{inv.recipient_name}</span>
                  <span className="text-xs text-muted-foreground ml-2">#{inv.invoice_number}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-amber-400">{inv.due_date}</span>
                  <span className="font-semibold">{(Number(inv.total_amount ?? inv.total) || 0).toFixed(2)} €</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {overdueInvoices.length > 0 && (
        <Card className="border-red-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-red-400">⚠ Erääntyneet laskut ({overdueInvoices.length} kpl)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {overdueInvoices.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium">{inv.recipient_name}</span>
                  <Badge variant="destructive" className="ml-2 text-[10px]">
                    {inv.status}
                  </Badge>
                </div>
                <span className="font-semibold text-red-400">
                  {(Number(inv.total_amount ?? inv.total) || 0).toFixed(2)} €
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
